import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import { Grid, Chip } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentWorkspaceUser } from 'wisestep-api';
import {
  IWorkspace,
  WsButton,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  Label,
  WsColors,
} from 'wisestep-base';

import {
  PermissionContextProvider,
  useWorkspaceContext,
  useWorkspaceSKUContext,
} from '../../context';
import { useNewEmailStore } from '../../features/emailSettings/components/twoWayEmailSync/Util/NewEmailStore';
import useCareerPageSettings from '../../hooks/useCareerPageSettings';
import GlobalLoader from '../../interceptor/useAxiosLoader';
// import { actionFilterPipelineStore } from '../../utils/ActionFiltersPiplineStore';
import { paginationSettingsStore } from '../../utils/PaginationSettingStore';
import { useConversationWidgetStore } from '../../utils/useConversationWidgetStore';
import BackToTop from '../BackToTop';
import RenewalNotice from '../RenewalNotice/RenewalNotice';

import MenuList from './MenuList';
import TopMenu from './TopMenu';

type LayoutProps = {
  isAdmin?: boolean;
  children: React.ReactNode;
};

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}
const CustomChip = styled(Chip)(() => ({
  borderRadius: 4,
  marginBottom: '10px',
  marginTop: '5px',
  height: '28px',
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  backgroundColor: WsColors.White,

  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)})`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)})`,
  },
});
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

const AppLayoutNew = ({ children, isAdmin = false }: LayoutProps) => {
  const [open, setOpen] = React.useState(false);
  const [workspacesList, setWorkspacesList] = useState<IWorkspace[]>([]);

  const isWidgetOpen = useConversationWidgetStore((state) => state.isWidgetOpen);
  const unReadCount = useNewEmailStore((state) => state.unReadCount);

  const navigate = useNavigate();
  const workspaceContext = useWorkspaceContext();
  const workspaceSKUContext = useWorkspaceSKUContext();
  const toggleDrawer = () => {
    setOpen(!open);
  };
  const toggleFromMenuList = (val: boolean) => setOpen(val);

  useEffect(() => {
    if (workspaceContext.id && !workspaceContext.owns && !workspaceContext.accepted) {
      navigate('/workspace/list');
    }
  }, [navigate, workspaceContext]);

  useEffect(() => {
    const url = window.location.origin.split('.');
    if (
      url[0].split('//')[1] === workspaceContext.link &&
      workspaceContext['onBoardingQuestionAnswered'] === false &&
      workspaceContext.owns === true
    ) {
      navigate('/onboarding');
    }
  }, [navigate, workspaceContext]);

  const getName = (name: string) => {
    return (name ?? '')
      .split(/[ -]/)
      .map((nameSubStr, index) => {
        if (index < 2) return nameSubStr[0];
      })
      .join('');
  };

  useEffect(() => {
    const loadWorkspaces = async () => {
      const workspaceReq = await getCurrentWorkspaceUser();
      setWorkspacesList(workspaceReq.data);
    };
    loadWorkspaces();
  }, []);

  // actionFilterPipelineStore();
  // const setActionFilterPipeline = actionFilterPipelineStore(
  //   (state) => state.setActionFilterPipeline
  // );
  // setActionFilterPipeline();
  useCareerPageSettings();
  paginationSettingsStore();
  const setPaginationsSettings = paginationSettingsStore((state) => state.setPaginationSettings);
  setPaginationsSettings();
  return (
    <>
      <Box sx={{ display: 'flex', visibility: isWidgetOpen ? 'hidden' : 'visible' }}>
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <GlobalLoader />
          <Toolbar
            sx={{
              pr: '24px',
            }}
          >
            <Tooltip title="Menu">
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={toggleDrawer}
                sx={{
                  marginRight: '36px',
                  ...(open && { display: 'none' }),
                }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>

            <Grid container direction="row" spacing={1}>
              <Grid item xs="auto">
                <CustomChip
                  sx={{ backgroundColor: WsColors.TheBlues100 }}
                  label={
                    <Label
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: WsColors.TheBlues500,
                      }}
                    >
                      {getName(workspaceContext.name ?? '').toUpperCase()}
                    </Label>
                  }
                />
              </Grid>
              <Grid item xs="auto" mt={1}>
                <WsTypography
                  bold={true}
                  variant={WsTypoVariants.TITLE}
                  color={WsTypoColors.TERTIARY}
                >
                  {workspaceContext.name}
                </WsTypography>
              </Grid>
              <Grid item xs="auto">
                <RenewalNotice />
              </Grid>
            </Grid>
            <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
              {''}
            </Typography>

            {/* <TeamAvatartGroup owner={workspaceContext.owns}></TeamAvatartGroup> */}

            {workspaceSKUContext?.workspacePlan?.planName === 'Trial' && (
              <Box sx={{ ml: 2, mr: 1 }}>
                <WsButton variant="text" onClick={() => navigate('/plan/purchase')}>
                  Upgrade Plan
                </WsButton>
              </Box>
            )}

            <TopMenu isAdmin={isAdmin} workspacesList={workspacesList} />
          </Toolbar>
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <Toolbar
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: [1],
            }}
          >
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component="nav" style={{ minHeight: 'calc(100vh - 65px)' }}>
            <PermissionContextProvider>
              <MenuList
                open={open}
                toggleDrawer={toggleFromMenuList}
                closeDrawer={() => setOpen(false)}
                isAdmin={isAdmin}
                unReadCount={unReadCount}
              />
            </PermissionContextProvider>
          </List>
        </Drawer>
        <Grid
          container
          item
          xs={12}
          spacing={0}
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
            overflow: 'auto',
          }}
          height="100%"
        >
          <Toolbar />
          <Box component="main" maxWidth="xl" width="100%" p={2} height="100%" id="main">
            {children}
          </Box>
        </Grid>
      </Box>
      <BackToTop />
    </>
  );
};

export default AppLayoutNew;
